import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Shield, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

const AuthCallbackPage = () => {
  const { language } = useLanguage();
  const { checkAuthStatus, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const processedRef = useRef(false);
  
  const [status, setStatus] = useState('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [action, setAction] = useState('');

  const translations = {
    tr: {
      processing: 'İşleminiz gerçekleştiriliyor...',
      successRegister: 'Google ile kayıt başarılı!',
      successLogin: 'Google ile giriş başarılı!',
      redirecting: 'Dashboard\'a yönlendiriliyorsunuz...',
      error: 'Bir hata oluştu',
      emailExists: 'Bu e-posta adresi zaten kayıtlı. Lütfen e-posta/şifre ile giriş yapın.',
      goToLogin: 'Giriş Sayfasına Git',
    },
    en: {
      processing: 'Processing your request...',
      successRegister: 'Google registration successful!',
      successLogin: 'Google login successful!',
      redirecting: 'Redirecting to dashboard...',
      error: 'An error occurred',
      emailExists: 'This email is already registered. Please login with email/password.',
      goToLogin: 'Go to Login',
    }
  };

  const txt = translations[language] || translations.en;

  useEffect(() => {
    // Prevent double processing
    if (processedRef.current) return;
    processedRef.current = true;

    const processCallback = async () => {
      const token = searchParams.get('token');
      const error = searchParams.get('error');
      const errorCode = searchParams.get('error_code');
      const actionParam = searchParams.get('action');
      
      setAction(actionParam || 'login');

      // Handle error from API
      if (error) {
        setStatus('error');
        if (errorCode === 'EMAIL_EXISTS') {
          setErrorMessage(txt.emailExists);
        } else {
          setErrorMessage(error);
        }
        return;
      }

      // If no token and no error, redirect to login immediately
      if (!token) {
        navigate('/login', { replace: true });
        return;
      }

      // Token exists - this means Google OAuth redirect happened
      // The cookie should already be set by the backend
      // Just verify the session and redirect to dashboard
      try {
        await checkAuthStatus();
        setStatus('success');
        // Redirect to dashboard immediately
        navigate('/dashboard', { replace: true });
      } catch (err) {
        setStatus('error');
        setErrorMessage(err.message || txt.error);
      }
    };

    processCallback();
  }, []);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="auth-callback-page">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-cyan-500/5"></div>
      <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-lg blur opacity-75"></div>
              <div className="relative bg-gradient-to-r from-emerald-600 to-cyan-600 p-3 rounded-lg">
                <Shield className="h-8 w-8 text-white" />
              </div>
            </div>
            <span className="text-2xl font-bold tracking-tight bg-gradient-to-r from-emerald-500 to-cyan-500 bg-clip-text text-transparent">
              Threat.live
            </span>
          </div>
        </div>

        <Card className="border-0 bg-gradient-to-br from-card to-muted/30 shadow-2xl">
          <CardContent className="pt-8 pb-8">
            {status === 'loading' && (
              <div className="text-center">
                <Loader2 className="h-12 w-12 animate-spin text-emerald-500 mx-auto mb-4" />
                <p className="text-lg font-medium">{txt.processing}</p>
              </div>
            )}

            {status === 'success' && (
              <div className="text-center">
                <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                </div>
                <h3 className="text-lg font-semibold mb-2">
                  {action === 'register' ? txt.successRegister : txt.successLogin}
                </h3>
                <p className="text-muted-foreground text-sm mb-4">{txt.redirecting}</p>
                <Loader2 className="h-5 w-5 animate-spin text-emerald-500 mx-auto" />
              </div>
            )}

            {status === 'error' && (
              <div className="text-center">
                <Alert variant="destructive" className="mb-4 bg-red-500/10 border-red-500/30">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
                <Button 
                  onClick={() => navigate('/login')}
                  className="w-full bg-gradient-to-r from-emerald-600 to-cyan-600"
                >
                  {txt.goToLogin}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuthCallbackPage;
